import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { useIsAr, useSelectedTheme } from '../../../../../Hooks';

import {
  CreatePropertyUnitType,
  GetAllPropertyUnitTypeAPI,
  GetLookupItemsByLookupTypeName,
} from '../../../../../Services';
import {
  CustomMenu,
  PropertyUnitTypeSelectorSkeleton,
} from '../../../../../Components';
import { useVerticalNav } from '../../../../../Contexts/VerticalNavContext';

import { CloseXIcon, PlusIcon } from '../../../../../assets/icons';

import useStyle from './styles';

export default function UnitTypeSelector({ propertyId }) {
  const styles = useStyle();
  const { setAlertBoxContent } = useVerticalNav();
  const theme = useSelector((store) => store.theme);
  const { palette } = useSelectedTheme().theme;
  const { isAr } = useIsAr(theme.themeDirection);

  const [unitTypes, setUnitTypes] = useState([]);

  const [unitTypesList, setUnitTypesList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchPropertyUnitTypes = async () => {
    const res = await GetAllPropertyUnitTypeAPI(propertyId);
    if (!(res && res.status && res.status !== 200)) {
      setUnitTypes(res?.[0]?.propertyUnitType || []);
    } else {
      setUnitTypes([]);
    }
  };

  const fetchUnitTypesList = async () => {
    setIsLoading(true);
    const res = await GetLookupItemsByLookupTypeName({
      lookUpName: 'UnitType',
      pageIndex: 1,
      pageSize: 10000,
    });

    if (!(res && res.status && res.status !== 200)) {
      setUnitTypesList(res.result || []);
    } else {
      setUnitTypesList([]);
    }
    setIsLoading(false);
  };

  const getFilteredUnitTypes = () => {
    const selectedIds = unitTypes.map((item) => item.lookupItemId);
    return unitTypesList.filter(
      (unit) => !selectedIds.includes(unit.lookupItemId)
    );
  };

  const addDeleteUnitTypeHandler = async (
    { addOpration, unitType },
    newUnitTypes
  ) => {
    setIsLoading(true);
    const payload = {
      propertyId,
      propertyUnitType: addOpration
        ? [
            ...(newUnitTypes ?? unitTypes),
            {
              lookupItemName: unitType?.lookupItemName,
              lookupItemId: unitType?.lookupItemId,
            },
          ]
        : newUnitTypes ?? unitTypes,
    };

    const res = await CreatePropertyUnitType(payload);

    if (!(res && res.status && res.status !== 200)) {
      setAlertBoxContent({
        display: true,
        variant: 'success',
        title: addOpration
          ? 'Property Unit Type created successfully'
          : 'Property Unit Type deleted successfully',
        onClose: () => setAlertBoxContent(null),
      });
      fetchPropertyUnitTypes();
    } else {
      setAlertBoxContent({
        display: true,
        variant: 'error',
        title: addOpration
          ? 'Failed to create Property Unit Type'
          : 'Failed to delete Property Unit Type',
        onClose: () => setAlertBoxContent(null),
      });
    }

    setIsLoading(false);
  };

  const deleteUnitTypeHandler = (lookupItemId) => {
    const updatedUnitTypes = unitTypes.filter(
      (unit) => unit.lookupItemId !== lookupItemId
    );
    setUnitTypes(updatedUnitTypes);
    addDeleteUnitTypeHandler(
      { addOpration: false, unitType: null },
      updatedUnitTypes
    );
  };

  useEffect(() => {
    fetchPropertyUnitTypes();
    fetchUnitTypesList();
  }, []);

  return (
    <Box className={styles.SelectorContainer}>
      {isLoading ? (
        <PropertyUnitTypeSelectorSkeleton />
      ) : (
        <>
          <Typography className={styles.titleUnitType}>Units Type</Typography>
          <Box className={styles.unitTypeContainer}>
            {unitTypes?.map((unitType, index) => (
              <Box
                className={styles.unitTypeItem}
                key={index}
              >
                <Typography className={styles.unitType}>
                  {unitType.lookupItemName}
                </Typography>
                <Box className={styles.closeIcon}>
                  <CloseXIcon
                    onClick={() => deleteUnitTypeHandler(unitType.lookupItemId)}
                    width='12'
                    height='12'
                    fill={palette.foreground.quinary}
                  />
                </Box>
              </Box>
            ))}

            {getFilteredUnitTypes().length > 0 && (
              <Box className={styles.addUnitType}>
                <CustomMenu
                  menuItems={getFilteredUnitTypes().map((unitType) => ({
                    text: unitType.lookupItemName || '',
                    handler: () =>
                      addDeleteUnitTypeHandler({ addOpration: true, unitType }),
                  }))}
                  activeIconClass={styles.menuActiveIconClass}
                  defaultIconClass={styles.menuDefaultIconClass}
                  customAnchorOrigin={{
                    vertical: 'bottom',
                    horizontal: isAr ? 'right' : 'left',
                  }}
                  customTransformOrigin={{
                    vertical: 'top',
                    horizontal: isAr ? 'right' : 'left',
                  }}
                >
                  <PlusIcon
                    width='20'
                    height='20'
                    fill={palette.button.secondary_fg}
                  />
                </CustomMenu>
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
